import React from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { observer } from 'mobx-react'
import { Badge } from '@winnin/winnin_ui.components.badge'
import { Icon } from '@winnin/winnin_ui.components.icon'
import { ProviderIcon } from '@winnin/winnin_ui.components.provider-icon'
import { TooltipWrapper } from '@winnin/winnin_ui.components.tooltip-wrapper'

import AddVideoToCheckBox from 'components/AddVideoToCheckbox'
import BlockComponentsRole75Checker from 'components/BlockComponentsRole75Checker'
import LivePostTag from 'components/LivePostTag'
import ReasoningCreatorVideo from 'components/ReasoningCreatorVideo'
import { T, useTranslate } from 'lib/tolgee'
import { isBrandPageComponentHidden } from 'modules/winnin-lite/common/winnin-lite.utils'
import { UserStore } from 'stores'
import { TOLGEE_GLOBALS } from 'utils'

import LivePostDate from './components/LivePostDate'
import LivePostMetric from './components/LivePostMetric'
import CreatorCardOnHover from '../../components/CreatorCardOnHover'
import Thumbnail from '../../components/Thumbnail'
import TopicListSmall from '../../components/TopicListSmall'
import PROVIDERS from '../../consts/providers'
import numberUtils from '../../utils/numberUtils'
import './index.scss'

dayjs.extend(relativeTime)

const TOLGEE_PREFIX = 'VideoCard'

const handleViewValue = (views, hasViewsReaction) => {
  if (hasViewsReaction) {
    return numberUtils.format(views, 1)
  }
  return '--'
}

const handleLikesValue = (likes, hasLikesReaction, isLikesReactionDisabled) => {
  if (!hasLikesReaction) {
    return '--'
  }
  if (isLikesReactionDisabled) {
    return <Icon icon="eyeClosed" fill="var(--base_color1)" width={20} height={20} />
  }
  return numberUtils.format(likes, 1)
}

const getLikesTooltipMessage = isLikesReactionDisabled => {
  if (isLikesReactionDisabled) {
    return TOLGEE_GLOBALS.infoHiddenByCreator
  }
  return TOLGEE_GLOBALS.platformMetricNotApplicable
}

function VideoCard({
  title,
  thumbnail,
  views,
  hasViewsReaction,
  creator,
  creatorId,
  onClick,
  postId,
  onClickRemoveVideo,
  provider,
  publishedAt,
  territories,
  likes,
  hasLikesReaction,
  isLikesReactionDisabled,
  isReasoning = false,
  reasoning,
  isTrendingTag = false,
  noCreatorLink,
  noHover,
  live,
  isPublic = false,
  shouldEnableCreatorClick = false,
  onCreatorClickCallback,
  isPublicity,
  pinId,
  hideCheckbox = false,
}) {
  const t = useTranslate()

  const isVideoSelectHidden = isBrandPageComponentHidden('videoSelect')
  const isCreatorLinkHidden = isBrandPageComponentHidden('creatorLink')
  const areTopicLinksHidden = isBrandPageComponentHidden('topicLinks')

  const providerName = PROVIDERS[provider]?.NAME
  const isUserAllowed = UserStore?.userInfo?.role > 50
  const isScheduled = live?.state === 'scheduled'

  const isLikesTooltipEnabled = provider === 'tw' || isLikesReactionDisabled

  function handleCardClick() {
    if (isScheduled) {
      return
    }

    onClick?.()
  }

  return (
    <div>
      <div className="VideoCard" onClick={handleCardClick}>
        <div
          className={classNames('VideoCard-image', {
            'tooltipped tooltipped-s tooltipped-multiline': isScheduled,
          })}
          aria-label={t({
            key: `${TOLGEE_PREFIX}-scheduledLiveTooltip`,
            defaultValue: 'The video page will be available once the live starts.',
          })}
        >
          <div
            className={classNames('VideoCard-live-ads-badges-wrapper', {
              'VideoCard-live-ads-badges-with-remove': onClickRemoveVideo,
            })}
          >
            {isPublicity && (
              <TooltipWrapper tooltipMessage={t(TOLGEE_GLOBALS.paidPartnership)} tooltipStyle="tooltippedS">
                <Badge colorScheme="yellow" className="YellowBadge-customStyle">
                  <Icon icon="ads" fill="var(--base_color1)" width={11} height={16} />
                </Badge>
              </TooltipWrapper>
            )}
            <LivePostTag live={live} />
          </div>

          <div className="VideoCard-provider">
            {providerName && <ProviderIcon icon={provider} width={provider === 'yt' ? 28 : 24} />}
          </div>

          {!isPublic && !hideCheckbox && !isVideoSelectHidden && (
            <BlockComponentsRole75Checker isClickBlocked={!isUserAllowed}>
              <AddVideoToCheckBox
                id={postId}
                provider={provider}
                title={title}
                thumbnail={thumbnail}
                creatorName={creator?.title}
                pinId={pinId}
              />
            </BlockComponentsRole75Checker>
          )}

          {onClickRemoveVideo && (
            <span
              onClick={onClickRemoveVideo}
              className="remove-video tooltipped tooltipped-w tooltipped-multiline"
              aria-label="Remove from topic"
            >
              <Icon icon="close" width={16} height={16} fill="var(--base_color1)" />
            </span>
          )}

          <Thumbnail isFallbackText height={173} src={thumbnail} />
        </div>

        <div className="VideoCard-info">
          {title && (
            <div className={`tooltipped tooltipped-n tooltipped-multiline`} aria-label={title}>
              <div className="VideoCard-title">{title}</div>
            </div>
          )}

          <div className="VideoCard-info-grid">
            <Thumbnail alt={creator?.title} src={creator?.thumbnail} className="VideoCard-creator-thumb" />
            {isReasoning ? (
              <ReasoningCreatorVideo
                videoTitle={title}
                creator={creator}
                creatorId={creatorId}
                provider={provider}
                publishedAt={publishedAt}
                reasoning={reasoning}
                isTrendingTag={isTrendingTag}
                likes={likes || 0}
              />
            ) : (
              <>
                <div>
                  <CreatorCardOnHover
                    onCreatorClickCallback={onCreatorClickCallback}
                    videoTitle={title}
                    title={creator?.title}
                    id={creatorId}
                    noHover={noHover || isPublic}
                    provider={provider}
                    noLink={noCreatorLink || isCreatorLinkHidden || (isPublic && !shouldEnableCreatorClick)}
                    groupId={creator?.groupId}
                    type={creator?.__typename}
                  />

                  {live ? (
                    <LivePostDate live={live} />
                  ) : (
                    <div className="VideoCard-publishedAt">{dayjs(publishedAt).from(dayjs())}</div>
                  )}

                  <TopicListSmall
                    className="VideoCard-TopicListSmall"
                    territories={territories}
                    tooltippedText="Video Topic"
                    noLink={areTopicLinksHidden}
                  />
                </div>

                {live ? (
                  <LivePostMetric live={live} views={views} />
                ) : (
                  <div className="VideoCard-numbers VideoCard-views">
                    <strong>{handleViewValue(views, hasViewsReaction)}</strong>
                    <span>
                      <T keyName={`${TOLGEE_PREFIX}-labelViews`}>Views</T>
                    </span>
                  </div>
                )}

                <div
                  aria-label={t(getLikesTooltipMessage(isLikesReactionDisabled))}
                  className={classNames('VideoCard-numbers VideoCard-likes', {
                    'tooltipped tooltipped-n tooltipped-multiline': isLikesTooltipEnabled,
                  })}
                >
                  <strong>{handleLikesValue(likes, hasLikesReaction, isLikesReactionDisabled)}</strong>
                  <span>
                    <T keyName={`${TOLGEE_PREFIX}-labelLikes`}>Likes</T>
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default observer(VideoCard)
